import { Button, Skeleton } from 'antd';
import { observer } from 'mobx-react';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import Card, { CardButtonWrap } from 'components/Card';
import ErrorBox from 'components/ErrorBox';
import ScrollTop from 'components/ScrollTop';
import FullTransferDetail from 'modules/Transfer/components/TransferDetail/Full';
import TransferFrom from 'modules/Transfer/components/TransferFrom';
import { useBrokerList, useTransferInfo } from 'modules/Transfer/hooks';
import { BrokerData, TransferMethodEnum } from 'modules/Transfer/type';
import { PageIdEnum, TransferFormProps } from 'modules/Transfer/withTransferForm';
import security from 'stores/security';
import { otherBrokerOption } from '../../helper';
import { AcatsSaveReqParamsType, BrokerPageFormutil, CheckPageFormutil } from '../../type';
import { handleCheckPageParams } from '../helper';
import { usePostTransferSave } from '../hooks';

const Full: FC<TransferFormProps> = observer(({ getStorageDataByKey, setStorageDataByKey }) => {
    const { currentAccountId, currentAccount } = security;
    const { data } = useBrokerList({ getAllBroker: true });
    const navigate = useNavigate();
    const brokerPageData = getStorageDataByKey<BrokerPageFormutil>(PageIdEnum.BROKER);
    const checkPageData = getStorageDataByKey<CheckPageFormutil>(PageIdEnum.CHECK);
    const { broker_id: brokerId } = brokerPageData;
    const resultStorageCheckPageParams = handleCheckPageParams(checkPageData);
    const {
        files: storageFiles,
        broker_user_name: storageBrokerUserName,
        broker_account: storageBrokerAccount
    } = resultStorageCheckPageParams;
    const {
        data: transferInfoData,
        error: transferInfoError,
        isLoading: transferInfoLoading,
        mutate: transferInfoMutate
    } = useTransferInfo();
    const { isMutating, trigger } = usePostTransferSave({
        accountId: currentAccountId!,
        statementFiles: storageFiles
    });
    const handleEditBroker = () => {
        // 需要把broker和check页面的数据填充到对应的编辑页面中
        setStorageDataByKey(PageIdEnum.EDIT_BROKER, brokerPageData);
        setStorageDataByKey(PageIdEnum.EDIT_CHECK, checkPageData);

        navigate('../edit/broker');
    };
    const handleSubmit = () => {
        const currentBroker: BrokerData =
            (data?.items || []).find((item: BrokerData) => item.id === brokerId) || otherBrokerOption;
        const { license: brokerLicense, account_type: brokerAccountType } = currentBroker;
        const { account_no: accountNo } = transferInfoData || {};

        // 接口文档: https://tigertech.feishu.cn/wiki/ROHPwAR48irCxQkiaGCcqGCdnBf
        const params: AcatsSaveReqParamsType = {
            ...currentBroker,
            account_no: accountNo || '',
            real_name_en: storageBrokerUserName,
            account_type: brokerAccountType || 'Individual',
            side: 'IN',
            transfer_method: TransferMethodEnum.ACATS,
            // 文档上接口接受的字段是一个string, 然后经纪公司是other的时候brokerId是null, 这时候不需要传broker_id
            broker_id: brokerId ? String(brokerId) : undefined,
            userEnteredFirm: !brokerId, // 没有实际意义,brokerId是空值的时候是true
            license: brokerLicense || 'Marsco',
            total_assets: true, // 全转是true
            category: currentAccount!.category,
            ...resultStorageCheckPageParams
        };

        trigger(params);
    };

    const renderTransferDetail = () => {
        if (transferInfoError && !transferInfoData) {
            return <ErrorBox error={transferInfoError} onClick={() => transferInfoMutate()} />;
        }

        if (transferInfoLoading) {
            return <Skeleton active />;
        }

        return <FullTransferDetail accountNo={transferInfoData?.account_no || ''} />;
    };

    return (
        <Card form>
            <ScrollTop />
            <h3 className="transfer-title">{__('Confirm Information')}</h3>
            <TransferFrom
                linkOptions={{
                    onClickLink: handleEditBroker,
                    text: __('Edit')
                }}
                brokerInfo={{
                    brokerId: brokerId,
                    holderName: storageBrokerUserName,
                    account: storageBrokerAccount
                }}
            />
            {renderTransferDetail()}
            <CardButtonWrap className="mt-5">
                <Button size="large" onClick={() => navigate(-1)}>
                    {__('Back')}
                </Button>
                <Button size="large" type="primary" loading={isMutating} onClick={handleSubmit}>
                    {__('Submit')}
                </Button>
            </CardButtonWrap>
        </Card>
    );
});

export default Full;
